import { useId, type CSSProperties } from 'react'
import { Link } from 'react-router'
import { scaleLinear } from 'd3-scale'
import { HatchPattern } from '../../../platform/charts/HatchPattern'
import { fixedDecimals } from '../../../platform/fixedDecimals'
import { budgetDetailPath } from '../budgetPaths'
import { isOver, shownPct } from '../budgetRules'
import type { Envelope } from '../budgetWire'
import '../budget.css'

/**
 * Spent vs allowance (B1): one bar per envelope, solid is spent, hatched is what
 * is still yours. Hand-rolled SVG on `d3-scale` (rule 3); every skin is a class
 * in `budget.css` (rules 1 and 2) and the hatch reaches its fill through
 * `--ab-hatch` (a CSS fill beats a `fill="url()"` attribute).
 *
 * The solid length is the SERVER's percentage, capped at the track — nothing
 * here reads or adds money. An over envelope fills the whole track in the
 * over skin and says "over" in words; colour is never the only signal (rule 11).
 */

const TRACK_H = 160
const TARGET_W = 720
const MIN_PITCH = 56
const MAX_PITCH = 120
/** Caption text at --type-caption, in user units: a generous per-character width. */
const CHAR_W = 6.5
const TOP = 30
const BOTTOM = 24
const PLOT_H = TOP + TRACK_H + BOTTOM
const ROW_W = 300
const ROW_H = 12

const num = (n: number) => fixedDecimals(n, 2)

/** Truncates only when the column cannot hold the name. */
function clip(name: string, pitch: number): string {
  const max = Math.max(4, Math.floor((pitch - 8) / CHAR_W))
  return name.length > max ? `${name.slice(0, max - 1)}…` : name
}

function label(e: Envelope): string {
  return `${e.name}, ${shownPct(e)}% of allowance used${isOver(e) ? ', over' : ''}`
}

type Props = { envelopes: Envelope[]; phone: boolean; labelledBy: string }

export function AllowanceBars({ envelopes, phone, labelledBy }: Props) {
  const hatchId = `ab-hatch-${useId().replace(/:/g, '')}`

  if (phone) {
    const x = scaleLinear().domain([0, 100]).range([0, ROW_W]).clamp(true)
    return (
      <ul className="ab-rows" aria-labelledby={labelledBy}>
        {envelopes.map((e, i) => {
          // One pattern per row svg, so one id per row: a repeated id is invalid markup.
          const rowHatchId = `${hatchId}-${i}`
          const solid = x(e.progress.percentage)
          const over = isOver(e)
          return (
            <li key={e.id} className="ab-row">
              <Link to={budgetDetailPath(e.id)} className="ab-row__link" aria-label={label(e)}>
              <div className={over ? 'ab-row__label ab-row__label--over' : 'ab-row__label'}>
                <span className="ab-row__name">{e.name}</span>
                <span className="ab-row__pct">
                  {shownPct(e)}%{over ? ' over' : ''}
                </span>
              </div>
              <svg
                className="ab-row__plot"
                viewBox={`0 0 ${ROW_W} ${ROW_H}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
                style={{ '--ab-hatch': `url(#${rowHatchId})` } as CSSProperties}
              >
                <defs>
                  <HatchPattern id={rowHatchId} groundClass="ab-hatch__ground" stripeClass="ab-hatch__stripe" />
                </defs>
                <rect className={over ? 'ab-bar ab-bar--over' : 'ab-bar ab-bar--spent'} x="0" y="0" width={num(solid)} height={ROW_H} />
                {ROW_W - solid > 0 && (
                  <rect className="ab-left" x={num(solid)} y="0" width={num(ROW_W - solid)} height={ROW_H} />
                )}
                {solid > 0 && ROW_W - solid > 0 && (
                  <line className="ab-boundary" x1={num(solid)} y1="0" x2={num(solid)} y2={ROW_H} />
                )}
              </svg>
              </Link>
            </li>
          )
        })}
      </ul>
    )
  }

  const hatchVar = { '--ab-hatch': `url(#${hatchId})` } as CSSProperties
  const y = scaleLinear().domain([0, 100]).range([TRACK_H, 0]).clamp(true)
  const n = Math.max(1, envelopes.length)
  // The chart fills its panel: the pitch follows the count, so a handful of
  // envelopes get wide columns and a long list keeps a floor per column.
  const pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, TARGET_W / n))
  const barW = Math.min(44, pitch * 0.5)
  const width = n * pitch
  return (
    <svg
      className="ab-plot"
      role="list"
      aria-labelledby={labelledBy}
      viewBox={`0 0 ${width} ${PLOT_H}`}
      style={{ ...hatchVar, '--ab-w': `${width}px` } as CSSProperties}
    >
      <defs>
        <HatchPattern id={hatchId} groundClass="ab-hatch__ground" stripeClass="ab-hatch__stripe" />
      </defs>
      {envelopes.map((e, i) => {
        const x = i * pitch + (pitch - barW) / 2
        const mid = i * pitch + pitch / 2
        const solid = TRACK_H - y(e.progress.percentage)
        const rest = TRACK_H - solid
        const over = isOver(e)
        return (
          <g key={e.id} role="listitem">
            <Link className="ab-link" to={budgetDetailPath(e.id)} aria-label={label(e)}>
            <rect className="ab-hit" x={num(i * pitch)} y="0" width={num(pitch)} height={PLOT_H} />
            {over && (
              <text className="ab-over-word" x={num(mid)} y="8" textAnchor="middle">
                over
              </text>
            )}
            <text className={over ? 'ab-pct ab-pct--over' : 'ab-pct'} x={num(mid)} y="22" textAnchor="middle">
              {shownPct(e)}%
            </text>
            {rest > 0 && <rect className="ab-left" x={num(x)} y={num(TOP)} width={num(barW)} height={num(rest)} />}
            <rect
              className={over ? 'ab-bar ab-bar--over' : 'ab-bar ab-bar--spent'}
              x={num(x)}
              y={num(TOP + rest)}
              width={num(barW)}
              height={num(solid)}
            />
            {solid > 0 && rest > 0 && (
              <line className="ab-boundary" x1={num(x)} y1={num(TOP + rest)} x2={num(x + barW)} y2={num(TOP + rest)} />
            )}
            <text className="ab-name" x={num(mid)} y={TOP + TRACK_H + 16} textAnchor="middle">
              {clip(e.name, pitch)}
            </text>
            </Link>
          </g>
        )
      })}
    </svg>
  )
}
